// filepath: apps/web/src/features/observability/ObservabilityDashboardPage.tsx
//
// FR-UI-DASH-1 -- page chrome for the tenant observability dashboard.
//
// Composes the 4 widgets (AC-1 widgets 1-4), the Active Alerts panel
// (AC-3 + AC-3a), the alert-rule authoring modal trigger (AC-2), the
// manual refresh button + 60-second auto-refresh (AC-4), and the
// per-widget drill-down links (AC-5).
//
// 5-error-state contract (FR-UI-CORE-10):
//   - loading          -> SkeletonLayout
//   - error_fetch      -> FetchError (retry button)
//   - permission_denied-> PermissionDenied (403)
//   - stale_version    -> StaleVersionError
//   - rate_limit       -> RateLimitBackpressure
//
// All chrome is rendered as text nodes (INV-9). No client-side
// formula evaluation (FR-UI-NFR-2). tenantSlug flows from
// TenantProvider into the TanStack Query cache key as the 2nd
// segment (INV-1).

import React, { useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { ApiError } from "@/definitions/types";
import { SkeletonLayout } from "@/design-system/error-states/SkeletonLayout";
import { FetchError } from "@/design-system/error-states/FetchError";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { StaleVersionError } from "@/design-system/error-states/StaleVersionError";
import { RateLimitBackpressure } from "@/design-system/error-states/RateLimitBackpressure";
import { InstanceCountWidget } from "./widgets/InstanceCountWidget";
import { ErrorRateSparkline } from "./widgets/ErrorRateSparkline";
import { StepDurationHistogram } from "./widgets/StepDurationHistogram";
import { ProcessTypeBreakdownWidget } from "./widgets/ProcessTypeBreakdownWidget";
import { ActiveAlertsPanel } from "./ActiveAlertsPanel";
import { AlertRuleAuthoringDialog } from "./AlertRuleAuthoringDialog";
import {
  useMetricsSummary,
  useActiveAlerts,
  useProcessDefinitionBreakdown,
} from "./queries";
import type { TenantMetricsSummary } from "./types";

/** Classify a TanStack Query error into the 5-state contract. */
export type DashboardStatus =
  | "loading"
  | "error_fetch"
  | "error_permission_denied"
  | "error_stale_version"
  | "error_rate_limit"
  | "success";

export function classifyDashboardError(err: ApiError): DashboardStatus {
  if (err.status === 403) return "error_permission_denied";
  if (err.status === 404 || err.status === 410) return "error_stale_version";
  if (err.status === 429 || err.code === "rate_limited") {
    return "error_rate_limit";
  }
  return "error_fetch";
}

export interface ObservabilityDashboardPageProps {
  /** Optional override for the drill-down navigation (for tests). */
  onDrillDown?: (params: { from: string; to: string; metric: string }) => void;
  /** Optional override for the alert-rule authoring trigger. */
  onOpenAlertRuleAuthoring?: () => void;
}

export function ObservabilityDashboardPage({
  onDrillDown,
  onOpenAlertRuleAuthoring,
}: ObservabilityDashboardPageProps = {}): React.ReactElement {
  const { t } = useTranslation();
  const summaryQuery = useMetricsSummary();
  const alertsQuery = useActiveAlerts();
  const breakdownQuery = useProcessDefinitionBreakdown();
  const [alertAuthoringOpen, setAlertAuthoringOpen] = useState(false);

  // AC-4: loading state during initial fetch.
  if (
    summaryQuery.isLoading ||
    alertsQuery.isLoading ||
    breakdownQuery.isLoading
  ) {
    return (
      <main
        data-testid="observability-dashboard-page--loading"
        aria-labelledby="observability-dashboard-page-title"
        className="observability-dashboard-page observability-dashboard-page--loading"
      >
        <h1 id="observability-dashboard-page-title" className="visually-hidden">
          {t("observability.dashboard.title", "Tenant observability")}
        </h1>
        <SkeletonLayout />
      </main>
    );
  }

  // AC-4: error surfaces (5-state fetch contract).
  if (summaryQuery.error) {
    const status = classifyDashboardError(summaryQuery.error);
    return (
      <ErrorSurface
        status={status}
        error={summaryQuery.error}
        onRetry={() => {
          void summaryQuery.refetch();
        }}
      />
    );
  }
  if (alertsQuery.error) {
    const status = classifyDashboardError(alertsQuery.error);
    return (
      <ErrorSurface
        status={status}
        error={alertsQuery.error}
        onRetry={() => {
          void alertsQuery.refetch();
        }}
      />
    );
  }
  if (breakdownQuery.error) {
    const status = classifyDashboardError(breakdownQuery.error);
    return (
      <ErrorSurface
        status={status}
        error={breakdownQuery.error}
        onRetry={() => {
          void breakdownQuery.refetch();
        }}
      />
    );
  }

  const summary: TenantMetricsSummary = summaryQuery.data!;
  const alerts = alertsQuery.data ?? [];
  const breakdownRows = breakdownQuery.data?.rows ?? [];

  return (
    <main
      data-testid="observability-dashboard-page"
      aria-labelledby="observability-dashboard-page-title"
      className="observability-dashboard-page"
    >
      <header className="observability-dashboard-page-header">
        <h1
          id="observability-dashboard-page-title"
          data-testid="observability-dashboard-page-title"
          className="observability-dashboard-page-title"
        >
          {t("observability.dashboard.title", "Tenant observability")}
        </h1>
        <p className="observability-dashboard-page-subtitle">
          {t(
            "observability.dashboard.subtitle",
            "Aggregated metrics, error rates, and step durations for this tenant.",
          )}
        </p>
        <button
          type="button"
          className="btn btn-primary"
          data-testid="observability-dashboard-refresh"
          onClick={() => {
            void summaryQuery.refetch();
            void alertsQuery.refetch();
            void breakdownQuery.refetch();
          }}
        >
          {t("observability.dashboard.refresh", "Refresh now")}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          data-testid="observability-dashboard-author-alert"
          onClick={
            onOpenAlertRuleAuthoring ?? (() => setAlertAuthoringOpen(true))
          }
        >
          {t("observability.dashboard.author_alert", "Author alert rule")}
        </button>
      </header>

      <section
        className="observability-dashboard-widgets"
        data-testid="observability-dashboard-widgets"
      >
        <InstanceCountWidget summary={summary} onDrillDown={onDrillDown} />
        <ErrorRateSparkline summary={summary} onDrillDown={onDrillDown} />
        <StepDurationHistogram summary={summary} onDrillDown={onDrillDown} />
        <ProcessTypeBreakdownWidget rows={breakdownRows} />
      </section>

      <section
        className="observability-dashboard-alerts"
        data-testid="observability-dashboard-alerts"
      >
        <ActiveAlertsPanel alerts={alerts} />
      </section>

      {alertAuthoringOpen && (
        <AlertRuleAuthoringDialog
          onClose={() => setAlertAuthoringOpen(false)}
        />
      )}
    </main>
  );
}

function ErrorSurface({
  status,
  error,
  onRetry,
}: {
  status: DashboardStatus;
  error: ApiError;
  onRetry: () => void;
}): React.ReactElement {
  if (status === "error_permission_denied") {
    return <PermissionDenied />;
  }
  if (status === "error_stale_version") {
    return <StaleVersionError />;
  }
  if (status === "error_rate_limit") {
    return (
      <RateLimitBackpressure
        retryAfterSeconds={error.retryAfterSeconds ?? 30}
      />
    );
  }
  return <FetchError message={error.message} onRetry={onRetry} />;
}
